import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { useTranslations } from 'next-intl';
import { appRoutes } from 'config';
import { marketing } from 'config/global';
import { Link } from 'core/i18n/navigation';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faApple, faAndroid } from '@fortawesome/free-brands-svg-icons';
import {
  // TODO(SP-671): used by the commented-out Overview / Chrome Extension items.
  // faTableCells,
  // faPuzzlePiece,
  faCube,
  faBriefcase,
  faRobot,
  faHeart,
  // Hidden use-case items (Knowledge Retrieval / Fast Thinking) — re-enable with them.
  // faMagnifyingGlass,
  // faBolt,
  faDisplay,
  faGlobe,
  faChartColumn,
  faCirclePlus,
  faCrown,
  faLayerGroup,
  faLifeRing,
  faUserGroup,
  faEnvelope,
  faNewspaper,
  faClockRotateLeft,
  faGift,
} from '@fortawesome/pro-regular-svg-icons';
import type { HeaderAppearance } from './VisitorHeader';

// Locale-agnostic destinations per routeKey. next-intl's <Link> prepends the
// active locale prefix at render time (derived from the URL, so consistent on
// server & client) — hrefs here stay unprefixed, which avoids hydration
// mismatches and enables client-side soft navigation.
const UNPREFIXED_ROUTES = {
  HOME: appRoutes.HOME,
  LOGIN: appRoutes.LOGIN,
  PRIVACY_POLICY: appRoutes.PRIVACY_POLICY,
  TERMS_OF_USE: appRoutes.TERMS_OF_USE,
  HELP_CENTRE: appRoutes.HELP_CENTRE,
  PRODUCT_MINDFEED_CLASSIC: appRoutes.PRODUCT_MINDFEED_CLASSIC,
  PRODUCT_USE_CASES_MEMORY_KEEPING: appRoutes.PRODUCT_USE_CASES_MEMORY_KEEPING,
  ABOUT_US: appRoutes.ABOUT_US,
  BLOG: appRoutes.BLOG,
  PRICING_INDIVIDUAL: appRoutes.PRICING.INDIVIDUAL,
  PRICING_BUSINESS: appRoutes.PRICING.BUSINESS,
} as const;

export type RouteKey = keyof typeof UNPREFIXED_ROUTES;

export interface MenuLink {
  icon: IconDefinition;
  /** i18n key suffix under `nav.items` (resolves `.title` and `.desc`). */
  key: string;
  href: string;
  /** When set, resolves to the (locale-agnostic) route; <Link> adds the prefix. */
  routeKey?: RouteKey;
  /** Shows a "Coming soon" pill next to the title for not-yet-launched items. */
  comingSoon?: boolean;
  /** External destination — opens in a new tab with safe rel attributes. */
  external?: boolean;
  /** Appended to the resolved href — for sub-paths/anchors (e.g. "#contact-us"). */
  suffix?: string;
}

// Resolves a link's locale-agnostic destination: the route for `routeKey`,
// otherwise its static href, plus any `suffix` (sub-path or anchor).
export const resolveHref = (link: MenuLink): string =>
  (link.routeKey ? UNPREFIXED_ROUTES[link.routeKey] : link.href) +
  (link.suffix ?? '');

export const PRODUCT: MenuLink[] = [
  // TODO: https://mindfeed.atlassian.net/browse/SP-671
  // { icon: faTableCells, key: 'overview', href: '#' },
  {
    icon: faCube,
    key: 'classic',
    href: '#',
    routeKey: 'PRODUCT_MINDFEED_CLASSIC',
  },
  { icon: faBriefcase, key: 'business', href: '#', comingSoon: true },
  { icon: faRobot, key: 'agent', href: '#', comingSoon: true },
];

export const USE_CASES: MenuLink[] = [
  {
    icon: faHeart,
    key: 'memoryKeeping',
    href: '#',
    routeKey: 'PRODUCT_USE_CASES_MEMORY_KEEPING',
  },
  // Temporarily hidden until these use-case pages are ready (kept for re-enable).
  // { icon: faMagnifyingGlass, key: 'knowledge', href: '#', comingSoon: true },
  // { icon: faBolt, key: 'fastThinking', href: '#', comingSoon: true },
];

export const DOWNLOAD: MenuLink[] = [
  { icon: faGlobe, key: 'web', href: '#', routeKey: 'LOGIN' },
  {
    icon: faApple,
    key: 'ios',
    href: marketing.appStoreUri,
    external: true,
  },
  {
    icon: faAndroid,
    key: 'android',
    href: marketing.googlePlayUri,
    external: true,
  },
  {
    icon: faDisplay,
    key: 'macos',
    href: marketing.macAppStoreUri,
    external: true,
  },

  // TODO: https://mindfeed.atlassian.net/browse/SP-671
  // { icon: faPuzzlePiece, key: 'chromeExtension', href: '#' },
];

export const PLANS: ResourceSection[] = [
  {
    labelKey: 'nav.individuals',
    // Plan links land on the pricing page scrolled to the plan cards.
    items: [
      {
        icon: faGift,
        key: 'basic',
        href: '#',
        routeKey: 'PRICING_INDIVIDUAL',
        suffix: '#compare-features',
      },
      {
        icon: faCirclePlus,
        key: 'plus',
        href: '#',
        routeKey: 'PRICING_INDIVIDUAL',
        suffix: '#compare-features',
      },
      {
        icon: faCrown,
        key: 'pro',
        href: '#',
        routeKey: 'PRICING_INDIVIDUAL',
        suffix: '#compare-features',
      },
    ],
  },
  {
    labelKey: 'nav.compare',
    // Opens the full feature comparison table and scrolls to it.
    items: [
      {
        icon: faChartColumn,
        key: 'comparePricing',
        href: '#',
        routeKey: 'PRICING_INDIVIDUAL',
      },
    ],
  },
  {
    labelKey: 'nav.extend',
    items: [
      { icon: faLayerGroup, key: 'addOns', href: '#pro', comingSoon: true },
    ],
  },
];

// Resources mega-menu, grouped into labelled sections (Explore / Company /
// Updates). Changelog is just the blog's "changelog" category.
export interface ResourceSection {
  labelKey: string;
  items: MenuLink[];
}

export const RESOURCES: ResourceSection[] = [
  {
    labelKey: 'nav.explore',
    items: [
      {
        icon: faLifeRing,
        key: 'helpCentre',
        href: '#',
        routeKey: 'HELP_CENTRE',
      },
    ],
  },
  {
    labelKey: 'nav.company',
    items: [
      { icon: faUserGroup, key: 'about', href: '#', routeKey: 'ABOUT_US' },
      {
        icon: faEnvelope,
        key: 'contact',
        href: '#',
        routeKey: 'HELP_CENTRE',
        suffix: '#contact-us',
      },
    ],
  },
  {
    labelKey: 'nav.updates',
    items: [
      { icon: faNewspaper, key: 'blog', href: '#', routeKey: 'BLOG' },
      {
        icon: faClockRotateLeft,
        key: 'changelog',
        href: '#',
        routeKey: 'BLOG',
        suffix: '/category/changelog',
      },
    ],
  },
];

// Product mega-menu as sections (the PRODUCT/USE_CASES/DOWNLOAD arrays stay
// separate for the mobile accordion).
export const PRODUCT_SECTIONS: ResourceSection[] = [
  { labelKey: 'nav.product', items: PRODUCT },
  { labelKey: 'nav.useCases', items: USE_CASES },
  { labelKey: 'nav.download', items: DOWNLOAD },
];

// Every top-level dropdown, in header order. One shared panel renders the
// active menu's sections, so moving between triggers swaps content instead of
// closing/reopening (no flicker). Each menu's width sizes the shared panel.
export interface NavDropdown {
  key: string;
  labelKey: string;
  sections: ResourceSection[];
}

export const NAV_DROPDOWNS: NavDropdown[] = [
  { key: 'product', labelKey: 'nav.product', sections: PRODUCT_SECTIONS },
  { key: 'plans', labelKey: 'nav.plans', sections: PLANS },
  { key: 'resources', labelKey: 'nav.resources', sections: RESOURCES },
];

// One fixed width for every dropdown — wide enough for the longest content
// (Product's "Agent-ready context" title keeps its badge on one line).
const PANEL_WIDTH = 'min(880px, calc(100vw - 40px))';

const MenuItem = ({
  link,
  onNavigate,
}: {
  link: MenuLink;
  onNavigate?: () => void;
}) => {
  const t = useTranslations();
  const muted = link.comingSoon;
  const href = resolveHref(link);
  const inner = (
    <>
      <span
        className={classNames(
          styles.item.icon,
          muted ? styles.item.iconMuted : styles.item.iconActive,
        )}
      >
        <FontAwesomeIcon icon={link.icon} className={styles.item.glyph} />
      </span>
      <span className={styles.item.text}>
        <b
          className={classNames(
            styles.item.title,
            muted ? styles.item.titleMuted : styles.item.titleActive,
          )}
        >
          {t(`nav.items.${link.key}.title`)}
        </b>
        {/* Badge sits at the start of the description line so it reads as a
            status prefix ("SOON — Shared memory for teams"). */}
        <span
          className={classNames(
            styles.item.desc,
            muted ? styles.item.descMuted : styles.item.descActive,
          )}
        >
          {link.comingSoon && (
            <span className={styles.item.badge}>{t('nav.comingSoon')}</span>
          )}
          {t(`nav.items.${link.key}.desc`)}
        </span>
      </span>
    </>
  );

  // "Coming soon" items have no real destination yet, so we render them as a
  // non-interactive element instead of an <a href="#">. That keeps crawlers
  // from treating them as internal links to a placeholder anchor (no wasted
  // crawl/link signal), while the visual treatment stays identical.
  if (link.comingSoon) {
    return (
      <span
        className={classNames(styles.item.root, 'cursor-default')}
        aria-disabled="true"
      >
        {inner}
      </span>
    );
  }

  // External links keep a plain anchor (new tab). Internal links use next-intl's
  // <Link> for locale-aware hrefs (consistent SSR/client) + soft navigation.
  if (link.external) {
    return (
      <a
        href={href}
        className={styles.item.root}
        onClick={onNavigate}
        target="_blank"
        rel="noreferrer noopener"
      >
        {inner}
      </a>
    );
  }

  return (
    <Link href={href} className={styles.item.root} onClick={onNavigate}>
      {inner}
    </Link>
  );
};

// Thin, light dropdown caret that morphs (path `d` transition) up when open.
const Chevron = ({ open }: { open: boolean }) => (
  <svg
    viewBox="0 0 9 9"
    fill="none"
    className={classNames(styles.chevron, open && 'mf-caret-open')}
    aria-hidden="true"
  >
    <path
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.5"
      d="M7.278 3.389 4.5 6.167 1.722 3.389"
    />
  </svg>
);

const SectionLabel = ({
  children,
  first,
}: {
  children: string;
  first?: boolean;
}) => (
  <div className={classNames(styles.sectionLabel, first && 'mt-0.5')}>
    {children}
  </div>
);

// `themed` renders the solid (dark-text) tone by default and the transparent
// (white) tone under `.marketing-dark` — for marketing-theme-aware pages.
type NavTone = HeaderAppearance | 'themed';

const TRIGGER_TONE: Record<NavTone, string> = {
  transparent: 'text-white opacity-[0.82]',
  solid: 'text-mf-off-black-600 opacity-80',
  themed: classNames(
    'text-mf-off-black-600 opacity-80',
    'marketing-dark:text-white marketing-dark:opacity-[0.82]',
  ),
};

// Inset pill backgrounds: open state and hover state per tone.
const PILL_OPEN: Record<NavTone, string> = {
  transparent: 'bg-white/15',
  solid: 'bg-mf-light-gray-200',
  themed: 'bg-mf-light-gray-200 marketing-dark:bg-white/15',
};

const PILL_HOVER: Record<NavTone, string> = {
  transparent: 'group-hover:bg-white/15',
  solid: 'group-hover:bg-mf-light-gray-200',
  themed: classNames(
    'group-hover:bg-mf-light-gray-200',
    'marketing-dark:group-hover:bg-white/15',
  ),
};

interface Props {
  appearance: NavTone;
  className?: string;
}

// Renders one dropdown's sections: link columns + divider + feature card.
const DropdownContent = ({
  sections,
  onNavigate,
}: {
  sections: ResourceSection[];
  onNavigate: () => void;
}) => {
  const t = useTranslations();
  return (
    <div className={styles.resGrid}>
      {sections.slice(0, -1).map((section) => (
        <div key={section.labelKey}>
          <SectionLabel first>{t(section.labelKey)}</SectionLabel>
          {section.items.map((link) => (
            <MenuItem key={link.key} link={link} onNavigate={onNavigate} />
          ))}
        </div>
      ))}
      <div className={styles.divider} />
      {sections.slice(-1).map((section) => (
        <div key={section.labelKey} className={styles.featCard}>
          <div className={styles.feat.tag}>{t(section.labelKey)}</div>
          {section.items.map((link) => (
            <MenuItem key={link.key} link={link} onNavigate={onNavigate} />
          ))}
        </div>
      ))}
    </div>
  );
};

export const NavMenu = ({ appearance, className }: Props) => {
  const t = useTranslations();

  // Which dropdown is open. A single shared panel renders its content, so
  // moving between triggers just swaps content — no close/reopen flicker.
  const [active, setActive] = useState<string | null>(null);
  // Keep the last-open menu mounted through the close fade so content doesn't
  // vanish mid-animation.
  const lastKey = useRef<string>(NAV_DROPDOWNS[0].key);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancelClose = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = null;
  };
  const open = (key: string) => {
    cancelClose();
    lastKey.current = key;
    setActive(key);
  };
  // Small delay bridges the trigger→panel gap without flicker.
  const scheduleClose = () => {
    cancelClose();
    closeTimer.current = setTimeout(() => setActive(null), 120);
  };

  // Close on Escape while a menu is open.
  useEffect(() => {
    if (!active) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setActive(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [active]);

  // Index of the visible menu; the track translates to it (direction of travel
  // is implicit — a higher index slides content left, a lower index right).
  const shownIndex = Math.max(
    0,
    NAV_DROPDOWNS.findIndex((m) => m.key === (active ?? lastKey.current)),
  );
  const slideRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [panelHeight, setPanelHeight] = useState<number>();
  // Grow/shrink the fixed-width panel to the visible slide's height.
  useLayoutEffect(() => {
    const el = slideRefs.current[shownIndex];
    if (el) setPanelHeight(el.offsetHeight);
  }, [shownIndex]);

  const trigger = classNames(
    styles.trigger,
    TRIGGER_TONE[appearance],
    'hover:opacity-100 focus-visible:opacity-100',
  );
  // Inset pill: light gray on the white header, subtle translucent white on the
  // transparent/dark header. Fades in on hover (whole button is the target) and
  // stays on while the menu is open.
  const pillBg = PILL_OPEN[appearance];
  const pill = classNames(styles.pill, PILL_HOVER[appearance]);

  return (
    <div
      className={classNames(styles.root, className)}
      onMouseLeave={scheduleClose}
      onMouseEnter={cancelClose}
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node)) setActive(null);
      }}
    >
      {NAV_DROPDOWNS.map((menu) => (
        <button
          key={menu.key}
          type="button"
          className={classNames(trigger, active === menu.key && 'opacity-100')}
          aria-haspopup="true"
          aria-expanded={active === menu.key}
          onMouseEnter={() => open(menu.key)}
          onFocus={() => open(menu.key)}
        >
          <span className={classNames(pill, active === menu.key && pillBg)}>
            {t(menu.labelKey)}
            <Chevron open={active === menu.key} />
          </span>
        </button>
      ))}

      {/* Single fixed-width panel. All menus sit side by side in a track that
          slides horizontally to the active one, and the panel height animates
          to the visible slide. */}
      <div
        className={classNames(
          styles.wrap,
          active ? styles.wrapOpen : styles.wrapClosed,
        )}
      >
        <div
          className={styles.panel}
          style={{ width: PANEL_WIDTH, height: panelHeight }}
        >
          <div
            className={styles.track}
            style={{
              width: `${NAV_DROPDOWNS.length * 100}%`,
              transform: `translateX(-${
                (shownIndex * 100) / NAV_DROPDOWNS.length
              }%)`,
            }}
          >
            {NAV_DROPDOWNS.map((menu, i) => (
              <div
                key={menu.key}
                ref={(el) => {
                  slideRefs.current[i] = el;
                }}
                className={styles.slide}
                style={{
                  width: `${100 / NAV_DROPDOWNS.length}%`,
                  opacity: i === shownIndex ? 1 : 0,
                }}
                // Keep off-screen slides out of the tab order / a11y tree.
                inert={i !== shownIndex || undefined}
              >
                <DropdownContent
                  sections={menu.sections}
                  onNavigate={() => setActive(null)}
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const styles = {
  // No gap between items: the spacing is symmetric padding on each navitem so
  // the whole strip is one continuous hover target (no dead gaps that would
  // flicker the panel shut when moving from Product to Plans).
  // relative anchors the single shared panel; the trigger row is one continuous
  // hover target so moving between triggers never leaves the container.
  root: 'relative hidden items-center lg:flex',
  // Each trigger fills the bar height so the row has no hover gaps; `group` lets
  // the inset pill react to hovering anywhere on the button. Small px keeps the
  // pills from touching while the full-height button stays a continuous target.
  trigger:
    'group inline-flex h-16 cursor-pointer items-center border-0 bg-transparent px-[6px] text-[15px] font-medium leading-none transition-opacity duration-75',
  // Inset rounded background that shows the hover/open state (bg colour applied
  // per-appearance in the component).
  pill: 'inline-flex items-center gap-[5px] rounded-[12px] px-[12px] py-[8px] transition-colors duration-150',
  // pointer-events-none so the caret's transparent fill isn't a hover dead-zone
  // (with fill="none" only the stroke would be hoverable). The up/down morph is
  // driven by the `.mf-caret`/`.mf-caret-open` `d` transition in globals.css.
  chevron: 'mf-caret pointer-events-none size-2.5 text-current opacity-70',
  // Single shared panel wrapper. 13px top padding bridges the trigger→panel gap
  // so hover holds; open/closed toggle visibility + a subtle rise.
  wrap: classNames(
    'absolute left-1/2 top-full z-[70] -translate-x-1/2 pt-[13px]',
    'transition-[opacity,transform,visibility] duration-150 ease-out',
  ),
  wrapOpen: 'visible translate-y-0 opacity-100',
  wrapClosed: 'pointer-events-none invisible translate-y-2 opacity-0',
  panel:
    'overflow-hidden rounded-[20px] border border-[#dedede] bg-white shadow-[0_24px_64px_rgba(0,0,0,0.35)] transition-[height] duration-150 ease-out',
  // Horizontal track of all menus; slides to the active one. items-start so each
  // slide keeps its natural height (measured for the panel height animation).
  track: 'flex items-start transition-transform duration-75 ease-out',
  // Each slide also fades (inactive → opacity 0) so the swap isn't a blunt push.
  slide: 'flex-none transition-opacity duration-75 ease-out',
  // Three equal-width sections | divider. Shared by Product, Plans and
  // Resources. (Product is sized wide enough that "Agent-ready context" still
  // keeps its "Soon" badge on the same line at 1fr.)
  resGrid:
    'grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)_1px_minmax(0,1fr)] items-stretch gap-6 px-[26px] py-4',
  featCard: 'flex flex-col rounded-2xl bg-mf-blue-50 p-[14px]',
  divider: 'bg-[#dedede]',
  sectionLabel:
    'mb-1.5 mt-[14px] px-[11px] text-[11px] font-bold uppercase tracking-[0.1em] text-mf-gray-400',
  item: {
    root: 'group/mi flex items-start gap-[13px] rounded-[13px] px-[11px] py-[9px] transition-colors hover:bg-mf-blue-50',
    // Colour lives in the active/muted variants below so "coming soon" items
    // read as unavailable (greyed) rather than as a clickable option.
    icon: 'flex h-[30px] w-[30px] flex-shrink-0 items-center justify-center transition-transform duration-150 group-hover/mi:-translate-y-px',
    iconActive: 'text-mf-blue',
    iconMuted: 'text-mf-gray-300',
    glyph: 'h-[22px] w-[22px]',
    text: 'flex flex-col gap-0.5 pt-0.5',
    titleRow: 'flex items-center gap-2',
    title: 'text-[14.5px] font-semibold tracking-[-0.01em] transition-colors',
    titleActive: 'text-mf-off-black-600 group-hover/mi:text-mf-blue-600',
    titleMuted: 'text-mf-gray-400',
    // Small gradient pill — flows inline at the start of the description text.
    badge:
      'mr-1.5 inline-flex translate-y-[-1px] items-center whitespace-nowrap rounded-full bg-[linear-gradient(90deg,#2757BA,#7B5CE6)] px-2 py-[3px] align-middle text-[9px] font-bold uppercase leading-none tracking-[0.06em] text-white shadow-[0_2px_6px_rgba(39,87,186,0.35)]',
    desc: 'text-[12.5px] leading-[1.42]',
    descActive: 'text-mf-gray-400',
    descMuted: 'text-mf-gray-300',
  },
  feat: {
    // Blue uppercase tag used as the label inside feature cards.
    tag: 'mb-[7px] text-[10.5px] font-bold uppercase tracking-[0.12em] text-mf-blue-500',
  },
};
